import { useState, useEffect, useMemo } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Package, 
  AlertTriangle,
  FileSpreadsheet,
  Upload,
  Filter,
  BarChart3,
  Calendar,
  MapPin,
  ChevronDown
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { useLocation } from '@/contexts/LocationContext';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import * as XLSX from 'xlsx';
import { useProfitSettings } from '@/hooks/use-profit-settings';
import { calculateItemsProfitBatch } from '@/lib/profit-calculation';
import {
  fetchLatestMarsPriceMapForSkus,
  getMarsInventoryUnitPrice,
  getOnHoldUnitPrice,
} from '@/lib/mars-inventory-value';
import { useNavigate } from 'react-router-dom';
import {
  calculateDeadStockReport,
  DEAD_STOCK_THRESHOLD_DAYS,
  type DeadStockRow,
} from '@/lib/dead-stock';
import {
  calculateStockCoverageByCategory,
  formatMonthsOfStock,
  STOCK_COVERAGE_PERIOD_MONTHS,
  type StockCoverageReport,
} from '@/lib/stock-coverage';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type SalesRow = Database['public']['Tables']['sales']['Row'];
type SaleItemsRow = Database['public']['Tables']['sale_items']['Row'];
type ProductsRow = Database['public']['Tables']['products']['Row'];
type InventoryMovementsRow = Database['public']['Tables']['inventory_movements']['Row'];
type CustomersRow = Database['public']['Tables']['customers']['Row'];
type VisitsRow = Database['public']['Tables']['visits']['Row'];

const getDateRangeLabel = (dateRange: string, customDateFrom?: string, customDateTo?: string) => {
  switch (dateRange) {
    case 'thisMonth':
      return 'Bu ay';
    case 'lastMonth':
      return 'Geçen ay';
    case 'lastYear':
      return 'Geçen yıl';
    case 'custom':
      if (customDateFrom && customDateTo) {
        return `${customDateFrom} - ${customDateTo}`;
      }
      return 'Özel tarih';
    default:
      return `Son ${dateRange} gün`;
  }
};

export default function Reports() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { companyId, loading: authLoading } = useAuth();
  const { currentLocation, availableLocations, canSwitchLocation, setCurrentLocation } = useLocation();
  const { settings: profitSettings } = useProfitSettings();
  const [selectedLocation, setSelectedLocation] = useState('');
  const [dateRange, setDateRange] = useState('7');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [customDatesApplied, setCustomDatesApplied] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Real data states
  const [salesByDayData, setSalesByDayData] = useState<any[]>([]);
  const [salesByCategoryData, setSalesByCategoryData] = useState<any[]>([]);
  const [topProductsData, setTopProductsData] = useState<any[]>([]);
  const [inventoryTurnoverData, setInventoryTurnoverData] = useState<any[]>([]);
  const [deadStockData, setDeadStockData] = useState<DeadStockRow[]>([]);
  const [allDeadStockData, setAllDeadStockData] = useState<DeadStockRow[]>([]);
  const [stockCoverage, setStockCoverage] = useState<StockCoverageReport | null>(null);
  
  // New product analytics states
  const [productQuantityData, setProductQuantityData] = useState<any[]>([]);
  const [categoryReportsData, setCategoryReportsData] = useState<any[]>([]);
  const [inventoryValueData, setInventoryValueData] = useState<any[]>([]);
  const [lowStockData, setLowStockData] = useState<any[]>([]);
  const [pricePerformanceData, setPricePerformanceData] = useState<any[]>([]);
  
  // On-hold analytics states
  const [onHoldData, setOnHoldData] = useState<any[]>([]);
  const [onHoldByDurationData, setOnHoldByDurationData] = useState<any[]>([]);
  const [onHoldByCategoryData, setOnHoldByCategoryData] = useState<any[]>([]);
  const [visitsData, setVisitsData] = useState<Array<Pick<VisitsRow, 'id' | 'entered_at' | 'created_at'>>>([]);
  const [visitsChartView, setVisitsChartView] = useState<'weekly' | 'monthly'>('weekly');
  
  // Category sales breakdown with products
  const [categorySalesBreakdown, setCategorySalesBreakdown] = useState<any[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Keep reports scoped to the active top-bar location.
  useEffect(() => {
    setSelectedLocation(currentLocation?.id || '');
  }, [currentLocation?.id]);

  const effectiveLocationId = selectedLocation || currentLocation?.id || null;
  
  const [keyMetrics, setKeyMetrics] = useState({
    totalSales: 0,
    grossProfit: 0,
    inventoryTurnover: 0,
    deadStockValue: 0,
    totalInventoryValue: 0,
    marsInventoryValue: 0,
    totalProducts: 0,
    totalUnitsInStock: 0,
    averageMargin: 0,
    onHoldCount: 0,
    onHoldValue: 0,
    avgOnHoldDuration: 0
  });

  // Fetch all analytics data with pagination
  const fetchAnalyticsData = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError(null);

      let startDate: Date;
      let endDate: Date | null = null;

      // Handle different date range types
      if (dateRange === 'thisMonth') {
        // Bu ay: from beginning of month to today
        const now = new Date();
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
      } else if (dateRange === 'lastMonth') {
        // Geçen ay: from beginning of last month to end of last month
        const now = new Date();
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        endDate.setHours(23, 59, 59, 999);
      } else if (dateRange === 'lastYear') {
        // Geçen yıl: from beginning of last year to end of last year
        const now = new Date();
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now.getFullYear() - 1, 11, 31);
        endDate.setHours(23, 59, 59, 999);
      } else if (dateRange === 'custom') {
        // Custom date range: only proceed if dates are applied
        if (customDateFrom && customDateTo && customDatesApplied) {
          startDate = new Date(customDateFrom);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(customDateTo);
          endDate.setHours(23, 59, 59, 999);
        } else {
          // If custom is selected but not applied, don't fetch (shouldn't happen due to useEffect guard)
          // But if it does, use default to prevent errors
          startDate = new Date();
          startDate.setDate(startDate.getDate() - 7);
        }
      } else {
        // Default: days ago - validate that dateRange is a valid number
        const daysAgo = parseInt(dateRange);
        if (isNaN(daysAgo) || daysAgo <= 0) {
          // Fallback to 7 days if invalid
          startDate = new Date();
          startDate.setDate(startDate.getDate() - 7);
        } else {
          startDate = new Date();
          startDate.setDate(startDate.getDate() - daysAgo);
        }
      }

      // Validate dates before proceeding
      if (isNaN(startDate.getTime()) || (endDate && isNaN(endDate.getTime()))) {
        console.error('Invalid date calculated:', { dateRange, customDateFrom, customDateTo, customDatesApplied });
        setError('Geçersiz tarih aralığı seçildi');
        setLoading(false);
        return;
      }

      // Fetch converted onhold records to identify sales linked to onhold conversions
      // This helps us filter out 0 TL sales created during onhold conversion
      let convertedOnHoldData: any[] = [];
      let convertedOnHoldPage = 0;
      const pageSize = 1000;
      let convertedOnHoldHasMore = true;

      while (convertedOnHoldHasMore) {
        let convertedOnHoldQuery = supabase
          .from('on_holds')
          .select('linked_sale_id')
          .eq('company_id', companyId)
          .eq('status', 'converted')
          .not('linked_sale_id', 'is', null);

        if (effectiveLocationId) {
          convertedOnHoldQuery = convertedOnHoldQuery.eq('location_id', effectiveLocationId);
        }

        const { data: pageData, error: convertedOnHoldError } = await convertedOnHoldQuery
          .range(convertedOnHoldPage * pageSize, (convertedOnHoldPage + 1) * pageSize - 1);
        
        if (convertedOnHoldError) throw convertedOnHoldError;

        if (pageData && pageData.length > 0) {
          convertedOnHoldData = [...convertedOnHoldData, ...pageData];
          convertedOnHoldPage++;
          convertedOnHoldHasMore = pageData.length === pageSize;
        } else {
          convertedOnHoldHasMore = false;
        }
      }

      // Create a set of sale IDs that are linked to onhold conversions
      const onHoldLinkedSaleIds = new Set(
        convertedOnHoldData
          .map((onHold: any) => onHold.linked_sale_id)
          .filter(Boolean)
      );

      // Fetch sales data with pagination to get all records
      let salesData: any[] = [];
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        let query = supabase
          .from('sales')
          .select(`
            *,
            sale_items (
              *,
              products (name, category, price, current_cost, initial_cost, item_code)
            ),
            sale_payments (payment_type, amount),
            customers (first_name, last_name)
          `)
          .eq('company_id', companyId)
          .eq('status', 'completed')
          .gte('sale_datetime', startDate.toISOString())
          .order('sale_datetime', { ascending: false });

        if (effectiveLocationId) {
          query = query.eq('location_id', effectiveLocationId);
        }
        
        if (endDate) {
          query = query.lte('sale_datetime', endDate.toISOString());
        }
        
        const { data: pageData, error: salesError } = await query
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (salesError) throw salesError;

        if (pageData && pageData.length > 0) {
          salesData = [...salesData, ...pageData];
          page++;
          hasMore = pageData.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      // Filter out 0 TL sales that are linked to onhold conversions (these are inventory adjustment records)
      salesData = salesData.filter((sale) => {
        const isZeroAmount = Math.abs(sale.total_net || 0) < 0.01;
        const isLinkedToOnHold = onHoldLinkedSaleIds.has(sale.id);
        
        // Filter out if it's a zero amount sale linked to onhold conversion
        return !(isZeroAmount && isLinkedToOnHold);
      });

      // Fetch visits data for visits trend chart (weekly/monthly)
      let visitsDataRows: Array<Pick<VisitsRow, 'id' | 'entered_at' | 'created_at'>> = [];
      let visitsPage = 0;
      let visitsHasMore = true;

      while (visitsHasMore) {
        let visitsQuery = supabase
          .from('visits')
          .select('id, entered_at, created_at')
          .eq('company_id', companyId)
          .gte('created_at', startDate.toISOString())
          .order('created_at', { ascending: true });

        if (endDate) {
          visitsQuery = visitsQuery.lte('created_at', endDate.toISOString());
        }

        const { data: visitsPageData, error: visitsError } = await visitsQuery
          .range(visitsPage * pageSize, (visitsPage + 1) * pageSize - 1);

        if (visitsError) throw visitsError;

        if (visitsPageData && visitsPageData.length > 0) {
          visitsDataRows = [...visitsDataRows, ...(visitsPageData as Array<Pick<VisitsRow, 'id' | 'entered_at' | 'created_at'>>)];
          visitsPage++;
          visitsHasMore = visitsPageData.length === pageSize;
        } else {
          visitsHasMore = false;
        }
      }

      // Fetch all products for inventory analysis with pagination
      let productsData: any[] = [];
      let productsPage = 0;
      let productsHasMore = true;

      while (productsHasMore) {
        let productsQuery = supabase
          .from('products')
          .select('*')
          .eq('company_id', companyId);

        if (effectiveLocationId) {
          productsQuery = productsQuery.eq('location_id', effectiveLocationId);
        }

        const { data: pageData, error: productsError } = await productsQuery
          .range(productsPage * pageSize, (productsPage + 1) * pageSize - 1);

        if (productsError) throw productsError;

        if (pageData && pageData.length > 0) {
          productsData = [...productsData, ...pageData];
          productsPage++;
          productsHasMore = pageData.length === pageSize;
        } else {
          productsHasMore = false;
        }
      }

      // Fetch Mars prices for "Güncel Mars Fiyatlarına Göre Stok Değeri" (uses kar hesaplama ayarı tier)
      const uniqueSkus = [...new Set(productsData.map(p => p.item_code).filter(Boolean))];
      const marsPriceMap = await fetchLatestMarsPriceMapForSkus(uniqueSkus, profitSettings.marsPriceTier);

      // Fetch inventory movements with pagination
      let movementsData: any[] = [];
      let movementsPage = 0;
      let movementsHasMore = true;

      while (movementsHasMore) {
        let movementsQuery = supabase
          .from('inventory_movements')
          .select(`
            *,
            products (name, category, price, current_cost)
          `)
          .eq('company_id', companyId)
          .gte('created_at', startDate.toISOString());

        if (effectiveLocationId) {
          movementsQuery = movementsQuery.eq('location_id', effectiveLocationId);
        }

        const { data: pageData, error: movementsError } = await movementsQuery
          .range(movementsPage * pageSize, (movementsPage + 1) * pageSize - 1);

        if (movementsError) throw movementsError;

        if (pageData && pageData.length > 0) {
          movementsData = [...movementsData, ...pageData];
          movementsPage++;
          movementsHasMore = pageData.length === pageSize;
        } else {
          movementsHasMore = false;
        }
      }

      // Fetch movement history (all-time) for accurate dead stock inactivity day calculation.
      // Query by product ids instead of location_id because some legacy/manual movement
      // rows may have null location_id and still represent real activity.
      let movementHistoryData: any[] = [];
      const productIds: string[] = productsData.map((product) => product.id).filter(Boolean);
      const movementProductChunkSize = 200;

      for (let i = 0; i < productIds.length; i += movementProductChunkSize) {
        const productIdChunk = productIds.slice(i, i + movementProductChunkSize);
        let movementHistoryPage = 0;
        let movementHistoryHasMore = true;

        while (movementHistoryHasMore) {
          const { data: pageData, error: movementHistoryError } = await supabase
            .from('inventory_movements')
            .select('product_id, created_at')
            .eq('company_id', companyId)
            .in('product_id', productIdChunk)
            .range(movementHistoryPage * pageSize, (movementHistoryPage + 1) * pageSize - 1);

          if (movementHistoryError) throw movementHistoryError;

          if (pageData && pageData.length > 0) {
            movementHistoryData = [...movementHistoryData, ...pageData];
            movementHistoryPage++;
            movementHistoryHasMore = pageData.length === pageSize;
          } else {
            movementHistoryHasMore = false;
          }
        }
      }

      // Fetch on-hold items with pagination (active only for display)
      let onHoldData: any[] = [];
      let onHoldPage = 0;
      let onHoldHasMore = true;

      while (onHoldHasMore) {
        let onHoldQuery = supabase
          .from('on_holds')
          .select(`
            *,
            product:products(name, category, price, current_cost, initial_cost, item_code),
            customer:customers(first_name, last_name, phone)
          `)
          .eq('company_id', companyId)
          .eq('status', 'on_hold');

        if (effectiveLocationId) {
          onHoldQuery = onHoldQuery.eq('location_id', effectiveLocationId);
        }

        const { data: pageData, error: onHoldError } = await onHoldQuery
          .range(onHoldPage * pageSize, (onHoldPage + 1) * pageSize - 1);

        if (onHoldError) throw onHoldError;

        if (pageData && pageData.length > 0) {
          onHoldData = [...onHoldData, ...pageData];
          onHoldPage++;
          onHoldHasMore = pageData.length === pageSize;
        } else {
          onHoldHasMore = false;
        }
      }

      // Fetch all on-hold history to calculate latest on-hold activity per product
      let onHoldHistoryData: any[] = [];
      let onHoldHistoryPage = 0;
      let onHoldHistoryHasMore = true;
      const days180Ago = new Date();
      days180Ago.setDate(days180Ago.getDate() - 180);
      days180Ago.setHours(0, 0, 0, 0);

      while (onHoldHistoryHasMore) {
        let onHoldHistoryQuery = supabase
          .from('on_holds')
          .select('id, product_id, date_on_hold, status')
          .eq('company_id', companyId);

        if (effectiveLocationId) {
          onHoldHistoryQuery = onHoldHistoryQuery.eq('location_id', effectiveLocationId);
        }

        const { data: pageData, error: onHoldHistoryError } = await onHoldHistoryQuery
          .range(onHoldHistoryPage * pageSize, (onHoldHistoryPage + 1) * pageSize - 1);

        if (onHoldHistoryError) throw onHoldHistoryError;

        if (pageData && pageData.length > 0) {
          onHoldHistoryData = [...onHoldHistoryData, ...pageData];
          onHoldHistoryPage++;
          onHoldHistoryHasMore = pageData.length === pageSize;
        } else {
          onHoldHistoryHasMore = false;
        }
      }

      const recentOnHoldData = onHoldHistoryData.filter((row: { date_on_hold?: string | null }) => {
        if (!row?.date_on_hold) return false;
        return new Date(row.date_on_hold) >= days180Ago;
      });

      // Fetch sales from last 180 days for dead stock calculation (regardless of selected date range)
      let recentSalesData: any[] = [];
      let recentSalesPage = 0;
      let recentSalesHasMore = true;

      while (recentSalesHasMore) {
        let recentSalesQuery = supabase
          .from('sales')
          .select('id, sale_datetime, sale_items(product_id)')
          .eq('company_id', companyId)
          .eq('status', 'completed')
          .gte('sale_datetime', days180Ago.toISOString());

        if (effectiveLocationId) {
          recentSalesQuery = recentSalesQuery.eq('location_id', effectiveLocationId);
        }

        const { data: pageData, error: recentSalesError } = await recentSalesQuery
          .range(recentSalesPage * pageSize, (recentSalesPage + 1) * pageSize - 1);

        if (recentSalesError) throw recentSalesError;

        if (pageData && pageData.length > 0) {
          recentSalesData = [...recentSalesData, ...pageData];
          recentSalesPage++;
          recentSalesHasMore = pageData.length === pageSize;
        } else {
          recentSalesHasMore = false;
        }
      }

      // Fetch sales data from last 12 months for stock turnover calculation
      const days365Ago = new Date();
      days365Ago.setMonth(days365Ago.getMonth() - 12);
      days365Ago.setHours(0, 0, 0, 0);
      
      let last12MonthsSalesData: any[] = [];
      let last12MonthsSalesPage = 0;
      let last12MonthsSalesHasMore = true;

      while (last12MonthsSalesHasMore) {
        let last12MonthsSalesQuery = supabase
          .from('sales')
          .select(`
            *,
            sale_items (
              *,
              products (name, category, price, current_cost, initial_cost, item_code)
            )
          `)
          .eq('company_id', companyId)
          .eq('status', 'completed')
          .gte('sale_datetime', days365Ago.toISOString())
          .order('sale_datetime', { ascending: false });

        if (effectiveLocationId) {
          last12MonthsSalesQuery = last12MonthsSalesQuery.eq('location_id', effectiveLocationId);
        }

        const { data: pageData, error: last12MonthsSalesError } = await last12MonthsSalesQuery
          .range(last12MonthsSalesPage * pageSize, (last12MonthsSalesPage + 1) * pageSize - 1);

        if (last12MonthsSalesError) throw last12MonthsSalesError;

        if (pageData && pageData.length > 0) {
          last12MonthsSalesData = [...last12MonthsSalesData, ...pageData];
          last12MonthsSalesPage++;
          last12MonthsSalesHasMore = pageData.length === pageSize;
        } else {
          last12MonthsSalesHasMore = false;
        }
      }

      // Filter out 0 TL sales that are linked to onhold conversions from last 12 months sales
      last12MonthsSalesData = last12MonthsSalesData.filter((sale) => {
        const isZeroAmount = Math.abs(sale.total_net || 0) < 0.01;
        const isLinkedToOnHold = onHoldLinkedSaleIds.has(sale.id);
        return !(isZeroAmount && isLinkedToOnHold);
      });

      // Process the data
      processSalesAnalytics(salesData || []);
      processInventoryAnalytics(productsData || [], movementsData || [], last12MonthsSalesData || []);
      setStockCoverage(
        calculateStockCoverageByCategory({
          products: productsData || [],
          sales: last12MonthsSalesData || [],
        }),
      );
      processDeadStockAnalytics(
        productsData || [],
        onHoldData || [],
        recentOnHoldData || [],
        onHoldHistoryData || [],
        recentSalesData || [],
        movementHistoryData || [],
      );
      await processProductAnalytics(productsData || [], salesData || [], marsPriceMap);
      processOnHoldAnalytics(onHoldData || [], marsPriceMap);
      setVisitsData(visitsDataRows);
      
      // Show data if we have any sales, products, or meaningful analytics
      const hasSalesData = (salesData?.length || 0) > 0;
      const hasProductsData = (productsData?.length || 0) > 0;
      const hasVisitsData = visitsDataRows.length > 0;
      const hasAnyData = hasSalesData || hasProductsData || hasVisitsData;
      
      if (import.meta.env.DEV) {
        console.debug('[Reports DEBUG] context', {
          companyId,
          currentLocationId: currentLocation?.id,
          selectedLocation,
          effectiveLocationId,
          dateRange,
          startDate: startDate.toISOString(),
          endDate: endDate?.toISOString() ?? null,
          counts: {
            sales: salesData?.length ?? 0,
            products: productsData?.length ?? 0,
            visits: visitsDataRows?.length ?? 0,
            movements: movementsData?.length ?? 0,
            onHolds: onHoldData?.length ?? 0,
          },
          hasAnyData,
        });
      }
      setHasData(hasAnyData);
    } catch (err: any) {
      if (import.meta.env.DEV) {
        console.error('[Reports DEBUG] fetchAnalyticsData FAILED', {
          message: err?.message,
          code: err?.code,
          details: err?.details,
          hint: err?.hint,
          status: err?.status,
          companyId,
          currentLocationId: currentLocation?.id,
          selectedLocation,
          effectiveLocationId,
          raw: err,
        });
      }
      setError(`Rapor verileri yüklenirken bir hata oluştu: ${err?.message ?? 'unknown'}`);
      setHasData(false);
    } finally {
      setLoading(false);
    }
  };

  const processSalesAnalytics = (sales: any[]) => {
    // Sales by day
    const salesByDay = new Map<string, { sales: number; orders: number }>();
    let totalSales = 0;
    let totalProfit = 0;

    // Category sales tracking
    const categoryMap = new Map<string, number>();
    
    // Category breakdown with products
    type ProductBreakdown = {
      quantity: number;
      sales: number;
      profit: number;
    };
    const categoryBreakdownMap = new Map<string, Map<string, ProductBreakdown>>();
    
    // Product performance tracking
    const productMap = new Map<string, {
      name: string;
      sales: number;
      units: number;
      profit: number;
    }>();

    sales.forEach(sale => {
      const saleDate = new Date(sale.sale_datetime);
      const dateKey = saleDate.toLocaleDateString('tr-TR', { 
        day: '2-digit', 
        month: 'short' 
      });

      // Sales by day
      const dayData = salesByDay.get(dateKey) || { sales: 0, orders: 0 };
      dayData.sales += sale.total_net;
      dayData.orders += 1;
      salesByDay.set(dateKey, dayData);

      totalSales += sale.total_net;

      // Process sale items
      sale.sale_items?.forEach((item: any) => {
        const product = item.products;
        if (!product) return;

        const category = product.category || 'Kategorisiz';
        const productName = product.name;

        // Category analysis
        const categoryTotal = categoryMap.get(category) || 0;
        categoryMap.set(category, categoryTotal + item.line_total_net);

        // Category breakdown with products
        if (!categoryBreakdownMap.has(category)) {
          categoryBreakdownMap.set(category, new Map());
        }
        const categoryProductsMap = categoryBreakdownMap.get(category)!;
        
        if (!categoryProductsMap.has(productName)) {
          categoryProductsMap.set(productName, {
            quantity: 0,
            sales: 0,
            profit: 0
          });
        }
        
        const productBreakdown = categoryProductsMap.get(productName)!;
        productBreakdown.quantity += item.quantity;
        productBreakdown.sales += item.line_total_net;
        
        // Calculate profit
        const cost = item.cost_each || product.current_cost || product.initial_cost || 0;
        const salePrice = item.sale_price_each || product.price || 0;
        const profit = (salePrice - cost) * item.quantity;
        productBreakdown.profit += profit;

        // Product analysis
        const productKey = product.name;
        const productData = productMap.get(productKey) || {
          name: product.name,
          sales: 0,
          units: 0,
          profit: 0
        };
        
        productData.sales += item.line_total_net;
        productData.units += item.quantity;
        productData.profit += profit;
        totalProfit += profit;
        
        productMap.set(productKey, productData);
      });
    });

    // Convert maps to arrays
    const salesByDayArray = Array.from(salesByDay.entries())
      .map(([date, data]) => ({ date, ...data }))
      .slice(-7); // Last 7 days

    // Add empty days if no sales data
    if (salesByDayArray.length === 0) {
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        salesByDayArray.push({
          date: date.toLocaleDateString('tr-TR', { 
            day: '2-digit', 
            month: 'short' 
          }),
          sales: 0,
          orders: 0
        });
      }
    }

    const totalCategorySales = Array.from(categoryMap.values()).reduce((sum, val) => sum + val, 0);
    const salesByCategoryArray = totalCategorySales > 0 
      ? Array.from(categoryMap.entries())
          .map(([name, value]) => ({
            name: name || 'Kategorisiz',
            value: Math.round(value),
            percentage: Math.round((value / totalCategorySales) * 100),
            color: getRandomColor()
          }))
          .sort((a, b) => b.value - a.value)
      : [];

    const topProductsArray = Array.from(productMap.values())
      .sort((a, b) => b.sales - a.sales)
      .slice(0, 5)
      .map((product, index) => ({
        id: (index + 1).toString(),
        ...product,
        sales: Math.round(product.sales),
        profit: Math.round(product.profit)
      }));

    // Process category breakdown with products
    const categoryBreakdownArray = Array.from(categoryBreakdownMap.entries())
      .map(([category, productsMap]) => {
        const products = Array.from(productsMap.entries())
          .map(([productName, data]) => ({
            name: productName,
            quantity: data.quantity,
            sales: Math.round(data.sales),
            profit: Math.round(data.profit)
          }))
          .sort((a, b) => b.quantity - a.quantity); // Sort by quantity (most sold first)
        
        const totalQuantity = products.reduce((sum, p) => sum + p.quantity, 0);
        
        return {
          category: category || 'Kategorisiz',
          totalQuantity,
          totalSales: Math.round(products.reduce((sum, p) => sum + p.sales, 0)),
          products
        };
      })
      .sort((a, b) => b.totalQuantity - a.totalQuantity); // Sort categories by total quantity

    setSalesByDayData(salesByDayArray);
    setSalesByCategoryData(salesByCategoryArray);
    setTopProductsData(topProductsArray);
    setCategorySalesBreakdown(categoryBreakdownArray);
    
    setKeyMetrics(prev => ({
      ...prev,
      totalSales: Math.round(totalSales),
      grossProfit: Math.round(totalProfit)
    }));
  };

  const processInventoryAnalytics = (products: any[], movements: any[], last12MonthsSales: any[]) => {
    // Calculate inventory turnover
    const categoryTurnover = new Map<string, { sold: number; avgInventory: number; totalSoldQty: number }>();

    // If we have products but no movements, still show categories with zero turnover
    if (products.length > 0) {
      products.forEach(product => {
        const productMovements = movements.filter(m => m.product_id === product.id);
        const salesMovements = productMovements.filter(m => m.movement_type === 'sale_out');
        
        const totalSold = salesMovements.reduce((sum, m) => sum + m.qty, 0);
        const avgInventory = Math.max(product.quantity_on_hand || 0, 1);

        // Category turnover - use actual sales data for better accuracy
        const category = product.category || 'Kategorisiz';
        const categoryData = categoryTurnover.get(category) || { sold: 0, avgInventory: 0, totalSoldQty: 0 };
        
        // Calculate actual sold quantity from last 12 months sales data for this product (for turnover calculation)
        let actualSoldQty = 0;
        last12MonthsSales.forEach((sale: any) => {
          if (sale.sale_items) {
            sale.sale_items.forEach((item: any) => {
              if (item.product_id === product.id) {
                actualSoldQty += item.quantity || 0;
              }
            });
          }
        });
        
        categoryData.sold += actualSoldQty > 0 ? actualSoldQty : totalSold;
        categoryData.totalSoldQty += actualSoldQty > 0 ? actualSoldQty : totalSold;
        categoryData.avgInventory += avgInventory;
        categoryTurnover.set(category, categoryData);
      });
    }

    // Process category turnover - improve calculation
    const turnoverArray = Array.from(categoryTurnover.entries())
      .map(([category, data]) => {
        // Calculate turnover rate: total sold / average inventory
        // If no sales, turnover is 0
        const turnover = data.avgInventory > 0 && data.totalSoldQty > 0 
          ? data.totalSoldQty / data.avgInventory 
          : 0;
        
        let status = 'poor';
        if (turnover > 6) status = 'excellent';
        else if (turnover > 4) status = 'good';
        else if (turnover > 2) status = 'average';

        return {
          category: category || 'Kategorisiz',
          turnover: Number(turnover.toFixed(2)),
          status,
          soldQty: data.totalSoldQty,
          avgInventory: Math.round(data.avgInventory)
        };
      })
      .filter(item => item.avgInventory > 0) // Only show categories with inventory
      .sort((a, b) => b.turnover - a.turnover); // Sort by turnover descending

    setInventoryTurnoverData(turnoverArray);
    
    // Calculate overall inventory turnover rate
    const totalSoldQty = Array.from(categoryTurnover.values()).reduce((sum, data) => sum + data.totalSoldQty, 0);
    const totalAvgInventory = Array.from(categoryTurnover.values()).reduce((sum, data) => sum + data.avgInventory, 0);
    const overallTurnoverRate = totalAvgInventory > 0 && totalSoldQty > 0
      ? totalSoldQty / totalAvgInventory
      : 0;
    
    setKeyMetrics(prev => ({
      ...prev,
      inventoryTurnover: Number(overallTurnoverRate.toFixed(2))
    }));
  };

  const processDeadStockAnalytics = (
    products: any[],
    activeOnHoldItems: any[],
    recentOnHoldItems: any[],
    allOnHoldItems: any[],
    recentSales: any[],
    movementHistory: any[],
  ) => {
    const deadStockReport = calculateDeadStockReport({
      products,
      activeOnHoldItems,
      recentOnHoldItems,
      allOnHoldItems,
      recentSales,
      movementHistory,
      thresholdDays: DEAD_STOCK_THRESHOLD_DAYS,
    });

    setAllDeadStockData(deadStockReport.items);
    setDeadStockData(deadStockReport.items.slice(0, 10)); // Keep report card compact: top 10 only
    setKeyMetrics(prev => ({
      ...prev,
      deadStockValue: deadStockReport.totalValue,
    }));
  };

  const processProductAnalytics = async (products: any[], sales: any[], marsPriceMap: Map<string, number> = new Map()) => {
    let totalInventoryValue = 0; // Son alınan fiyata göre (current_cost/initial_cost)
    let marsInventoryValue = 0;  // Güncel Mars fiyatlarına göre (tier from kar hesaplama ayarı)
    let totalUnitsInStock = 0;
    let totalRevenue = 0;
    let totalCost = 0;
    
    // Category-based analytics
    const categoryStats = new Map<string, {
      totalQuantity: number;
      totalValue: number;
      totalSales: number;
      productCount: number;
      avgPrice: number;
    }>();

    // Product quantity and value analytics
    const productQuantityData: any[] = [];
    const lowStockItems: any[] = [];
    const inventoryValueData: any[] = [];
    const pricePerformanceData: any[] = [];
    
    // Track price performance from sale_items (actual sales data)
    const productMarginMap = new Map<string, {
      name: string;
      category: string;
      totalRevenue: number;
      totalCost: number;
      totalQuantity: number;
      avgMargin: number;
    }>();

    // Process products for inventory data
    products.forEach(product => {
      const quantity = product.quantity_on_hand || 0;
      const lastReceivedCost = product.current_cost || product.initial_cost || 0;
      const marsCost = getMarsInventoryUnitPrice(product, marsPriceMap);
      const price = product.price || 0;
      const valueLastReceived = quantity * lastReceivedCost;
      const valueMars = quantity * marsCost;
      const category = product.category || 'Kategorisiz';

      totalInventoryValue += valueLastReceived;
      marsInventoryValue += valueMars;
      totalUnitsInStock += quantity;

      // Category stats
      const categoryData = categoryStats.get(category) || {
        totalQuantity: 0,
        totalValue: 0,
        totalSales: 0,
        productCount: 0,
        avgPrice: 0
      };
      
      categoryData.totalQuantity += quantity;
      categoryData.totalValue += valueLastReceived;
      categoryData.productCount += 1;
      categoryData.avgPrice = (categoryData.avgPrice * (categoryData.productCount - 1) + price) / categoryData.productCount;
      categoryStats.set(category, categoryData);

      // Add to product quantity data
      if (quantity > 0) {
        productQuantityData.push({
          name: product.name || 'İsimsiz Ürün',
          quantity,
          value: Math.round(valueLastReceived),
          category
        });
      }

      // Low stock identification (assuming reorder point is 5 or 10% of max quantity)
      const reorderPoint = product.reorder_point || Math.max(5, quantity * 0.1);
      if (quantity <= reorderPoint && quantity > 0) {
        lowStockItems.push({
          id: product.id,
          name: product.name || 'İsimsiz Ürün',
          code: product.item_code || 'Kod Yok',
          currentStock: quantity,
          reorderPoint: Math.round(reorderPoint),
          category,
          value: Math.round(valueLastReceived)
        });
      }
    });
    
    // Process sales data to calculate actual profit margins from sale_items
    // Prepare items for batch profit calculation with Mars prices
    // Track mapping from (saleId, itemIndex) to profit result index
    const profitItems: Array<{
      sku: string | null | undefined;
      saleDate: Date | string;
      quantity: number;
      revenue: number;
      cost_each?: number;
      current_cost?: number;
    }> = [];
    
    const itemToProfitIndexMap = new Map<string, number>();
    let globalItemIndex = 0;
    
    // Build profit items array and mapping in the same order
    sales.forEach(sale => {
      sale.sale_items?.forEach((item: any, itemIndexInSale: number) => {
        const product = item.products;
        const salePrice = item.sale_price_each || product?.price || 0;
        const mapKey = `${sale.id}-${itemIndexInSale}`;
        itemToProfitIndexMap.set(mapKey, globalItemIndex++);
        
        profitItems.push({
          sku: product?.item_code || null,
          productName: product?.name || null,
          saleDate: sale.sale_datetime,
          quantity: item.quantity,
          revenue: salePrice > 0 ? salePrice * item.quantity : item.line_total_net,
          cost_each: item.cost_each,
          current_cost: product?.current_cost,
        });
      });
    });

    // Calculate profits (with or without Mars prices)
    const profitResults = profitSettings.enabled && profitSettings.marsPriceTier
      ? await calculateItemsProfitBatch(profitItems, profitSettings.marsPriceTier, true)
      : await calculateItemsProfitBatch(profitItems, null, false);

    // Process results using the mapping to ensure correct alignment
    sales.forEach(sale => {
      sale.sale_items?.forEach((item: any, itemIndexInSale: number) => {
        const product = item.products;
        if (!product) {
          return; // Skip items without products, don't increment index
        }
        
        // Map item back to its profit result using the original index
        const mapKey = `${sale.id}-${itemIndexInSale}`;
        const profitIndex = itemToProfitIndexMap.get(mapKey);
        
        // Get profit result if available, otherwise fallback to regular cost
        let itemCost = 0;
        if (profitIndex !== undefined && profitResults[profitIndex]) {
          itemCost = profitResults[profitIndex].cost;
        } else {
          // Fallback to regular cost calculation if mapping fails
          const cost = item.cost_each || product.current_cost || 0;
          itemCost = cost * item.quantity;
        }
        
        const salePrice = item.sale_price_each || product.price || 0;
        
        // Only calculate if we have valid sale price (cost can be 0 for free items)
        if (salePrice > 0) {
          const itemRevenue = salePrice * item.quantity;
          
          // Accumulate total revenue and cost for overall margin calculation
          totalRevenue += itemRevenue;
          totalCost += itemCost;
          
          // Track product-level margins for price performance
          const productKey = product.name;
          const productData = productMarginMap.get(productKey) || {
            name: product.name,
            category: product.category || 'Kategorisiz',
            totalRevenue: 0,
            totalCost: 0,
            totalQuantity: 0,
            avgMargin: 0
          };
          
          productData.totalRevenue += itemRevenue;
          productData.totalCost += itemCost;
          productData.totalQuantity += item.quantity;
          productData.avgMargin = productData.totalRevenue > 0 
            ? ((productData.totalRevenue - productData.totalCost) / productData.totalRevenue) * 100 
            : 0;
          
          productMarginMap.set(productKey, productData);
        }
        
        // Update category sales
        const category = product.category || 'Kategorisiz';
        const categoryData = categoryStats.get(category);
        if (categoryData) {
          categoryData.totalSales += item.line_total_net;
          categoryStats.set(category, categoryData);
        }
      });
    });
    
    // Convert product margin map to price performance data
    productMarginMap.forEach((data) => {
      if (data.totalQuantity > 0) {
        pricePerformanceData.push({
          name: data.name,
          price: data.totalRevenue / data.totalQuantity,
          cost: data.totalCost / data.totalQuantity,
          margin: Math.round(data.avgMargin),
          category: data.category
        });
      }
    });


    // Convert category stats to arrays
    const categoryReportsArray = Array.from(categoryStats.entries()).map(([category, data]) => ({
      category,
      totalQuantity: data.totalQuantity,
      totalValue: Math.round(data.totalValue),
      totalSales: Math.round(data.totalSales),
      productCount: data.productCount,
      avgPrice: Math.round(data.avgPrice),
      margin: data.avgPrice > 0 ? Math.round(((data.avgPrice - (data.totalValue / Math.max(data.totalQuantity, 1))) / data.avgPrice) * 100) : 0
    })).sort((a, b) => b.totalValue - a.totalValue);

    // Sort arrays
    productQuantityData.sort((a, b) => b.quantity - a.quantity);
    lowStockItems.sort((a, b) => a.currentStock - b.currentStock);
    pricePerformanceData.sort((a, b) => b.margin - a.margin);

    // Create inventory value breakdown by category
    const inventoryValueArray = Array.from(categoryStats.entries()).map(([category, data]) => ({
      category,
      value: Math.round(data.totalValue),
      percentage: totalInventoryValue > 0 ? Math.round((data.totalValue / totalInventoryValue) * 100) : 0,
      productCount: data.productCount
    })).sort((a, b) => b.value - a.value);

    // Update state
    setProductQuantityData(productQuantityData.slice(0, 20)); // Top 20 by quantity
    setCategoryReportsData(categoryReportsArray);
    setInventoryValueData(inventoryValueArray);
    setLowStockData(lowStockItems.slice(0, 15)); // Top 15 low stock items
    setPricePerformanceData(pricePerformanceData.slice(0, 15)); // Top 15 by margin

    // Update metrics - calculate average margin from total revenue and cost
    // Profit Margin = (Total Revenue - Total Cost) / Total Revenue * 100
    const averageMargin = totalRevenue > 0 
      ? ((totalRevenue - totalCost) / totalRevenue) * 100 
      : 0;
    
    setKeyMetrics(prev => ({
      ...prev,
      totalInventoryValue: Math.round(totalInventoryValue),
      marsInventoryValue: Math.round(marsInventoryValue),
      totalProducts: products.length,
      totalUnitsInStock,
      averageMargin: Math.round(averageMargin)
    }));
  };

  const processOnHoldAnalytics = (onHoldItems: any[], marsPriceMap: Map<string, number>) => {
    let totalOnHoldValue = 0;
    let totalOnHoldCount = 0;
    let totalDurationDays = 0;
    
    // Category-based on-hold analytics
    const categoryStats = new Map<string, {
      count: number;
      value: number;
      quantity: number;
      avgDuration: number;
    }>();

    // Duration-based analytics
    const durationRanges = {
      '1-7 days': 0,
      '8-30 days': 0,
      '31-90 days': 0,
      '90+ days': 0
    };

    // Individual on-hold items for detailed view
    const onHoldDetails: any[] = [];

    const now = new Date();

    onHoldItems.forEach(item => {
      const product = item.product;
      const customer = item.customer;
      const quantity = item.hold_quantity || 0;
      const onHoldDate = new Date(item.date_on_hold);
      const daysOnHold = Math.floor((now.getTime() - onHoldDate.getTime()) / (1000 * 60 * 60 * 24));
      
      const unitPrice = getOnHoldUnitPrice(product, marsPriceMap, item.hold_price);
      const itemValue = quantity * unitPrice;
      
      totalOnHoldValue += itemValue;
      totalOnHoldCount += quantity;
      totalDurationDays += daysOnHold;

      // Category statistics
      const category = product?.category || 'Kategorisiz';
      const categoryData = categoryStats.get(category) || {
        count: 0,
        value: 0,
        quantity: 0,
        avgDuration: 0
      };
      
      categoryData.count += 1;
      categoryData.value += itemValue;
      categoryData.quantity += quantity;
      categoryData.avgDuration = (categoryData.avgDuration * (categoryData.count - 1) + daysOnHold) / categoryData.count;
      categoryStats.set(category, categoryData);

      // Duration range classification
      if (daysOnHold <= 7) {
        durationRanges['1-7 days'] += 1;
      } else if (daysOnHold <= 30) {
        durationRanges['8-30 days'] += 1;
      } else if (daysOnHold <= 90) {
        durationRanges['31-90 days'] += 1;
      } else {
        durationRanges['90+ days'] += 1;
      }

      // Add to detailed list
      onHoldDetails.push({
        id: item.id,
        productName: product?.name || 'Bilinmeyen Ürün',
        customerName: customer 
          ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Bilinmeyen Müşteri'
          : 'Bilinmeyen Müşteri',
        customerPhone: customer?.phone || '',
        category: category,
        quantity,
        value: Math.round(itemValue),
        daysOnHold,
        onHoldDate: item.date_on_hold
      });
    });

    // Convert maps to arrays
    const categoryArray = Array.from(categoryStats.entries()).map(([category, data]) => ({
      category,
      count: data.count,
      value: Math.round(data.value),
      quantity: data.quantity,
      avgDuration: Math.round(data.avgDuration)
    })).sort((a, b) => b.value - a.value);

    const durationArray = Object.entries(durationRanges).map(([range, count]) => ({
      range,
      count,
      percentage: onHoldItems.length > 0 ? Math.round((count / onHoldItems.length) * 100) : 0
    }));

    // Sort details by days on hold (longest first)
    onHoldDetails.sort((a, b) => b.daysOnHold - a.daysOnHold);

    // Update state
    setOnHoldData(onHoldDetails);
    setOnHoldByCategoryData(categoryArray);
    setOnHoldByDurationData(durationArray);

    // Update metrics
    const avgDuration = onHoldItems.length > 0 ? totalDurationDays / onHoldItems.length : 0;
    
    setKeyMetrics(prev => ({
      ...prev,
      onHoldCount: totalOnHoldCount,
      onHoldValue: Math.round(totalOnHoldValue),
      avgOnHoldDuration: Math.round(avgDuration)
    }));
  };

  const getRandomColor = () => {
    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1', '#d084d0'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const getCategoryColor = (index: number) => {
    const colors = [
      { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', accent: 'bg-blue-500' },
      { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', accent: 'bg-green-500' },
      { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', accent: 'bg-purple-500' },
      { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', accent: 'bg-orange-500' },
      { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', accent: 'bg-pink-500' },
      { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', accent: 'bg-indigo-500' },
      { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', accent: 'bg-teal-500' },
      { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', accent: 'bg-amber-500' },
    ];
    return colors[index % colors.length];
  };

  const visitsTrendData = useMemo(() => {
    const grouped = new Map<string, { label: string; periodStart: Date; visits: number }>();

    visitsData.forEach((visit) => {
      const rawDate = visit.entered_at || visit.created_at;
      if (!rawDate) return;

      const visitDate = new Date(rawDate);
      if (Number.isNaN(visitDate.getTime())) return;

      if (visitsChartView === 'weekly') {
        // Week starts on Monday for reporting consistency.
        const weekStart = new Date(visitDate);
        const dayIndex = (weekStart.getDay() + 6) % 7;
        weekStart.setDate(weekStart.getDate() - dayIndex);
        weekStart.setHours(0, 0, 0, 0);

        const key = weekStart.toISOString().slice(0, 10);
        const existing = grouped.get(key);
        if (existing) {
          existing.visits += 1;
        } else {
          grouped.set(key, {
            label: weekStart.toLocaleDateString('tr-TR', {
              day: '2-digit',
              month: 'short',
            }),
            periodStart: weekStart,
            visits: 1,
          });
        }
      } else {
        const monthStart = new Date(visitDate.getFullYear(), visitDate.getMonth(), 1);
        monthStart.setHours(0, 0, 0, 0);
        const key = `${monthStart.getFullYear()}-${monthStart.getMonth()}`;
        const existing = grouped.get(key);
        if (existing) {
          existing.visits += 1;
        } else {
          grouped.set(key, {
            label: monthStart.toLocaleDateString('tr-TR', {
              month: 'short',
              year: 'numeric',
            }),
            periodStart: monthStart,
            visits: 1,
          });
        }
      }
    });

    return Array.from(grouped.values())
      .sort((a, b) => a.periodStart.getTime() - b.periodStart.getTime())
      .map((item) => ({ label: item.label, visits: item.visits }));
  }, [visitsData, visitsChartView]);

  useEffect(() => {
    if (!authLoading && companyId) {
      // Only fetch if not custom or if custom dates have been applied
      // Also ensure custom dates are valid if using custom range
      if (dateRange !== 'custom' || (customDatesApplied && customDateFrom && customDateTo)) {
        void fetchAnalyticsData();
      }
    }
  }, [companyId, authLoading, dateRange, customDatesApplied, customDateFrom, customDateTo, profitSettings.marsPriceTier, effectiveLocationId]);

  // Handle custom date query button
  const handleCustomDateQuery = () => {
    if (customDateFrom && customDateTo) {
      if (new Date(customDateFrom) > new Date(customDateTo)) {
        toast({
          title: "Hata",
          description: "Başlangıç tarihi bitiş tarihinden sonra olamaz",
          variant: "destructive",
        });
        return;
      }
      setCustomDatesApplied(true);
      void fetchAnalyticsData();
    } else {
      toast({
        title: "Hata",
        description: "Lütfen başlangıç ve bitiş tarihlerini seçin",
        variant: "destructive",
      });
    }
  };

  // Reset filters to default
  const handleResetFilters = () => {
    setDateRange('7');
    setCustomDateFrom('');
    setCustomDateTo('');
    setCustomDatesApplied(false);
    setSelectedLocation(currentLocation?.id || '');
  };

  const getTurnoverBadgeVariant = (status: string) => {
    switch (status) {
      case 'excellent': return 'default';
      case 'good': return 'secondary';
      case 'average': return 'outline';
      case 'poor': return 'destructive';
      default: return 'outline';
    }
  };

  const getTurnoverLabel = (status: string) => {
    switch (status) {
      case 'excellent': return 'Mükemmel';
      case 'good': return 'İyi';
      case 'average': return 'Orta';
      case 'poor': return 'Zayıf';
      default: return 'Bilinmiyor';
    }
  };

  const handleExportExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Sales by day sheet
    const salesSheet = XLSX.utils.json_to_sheet(salesByDayData.map(item => ({
      'Tarih': item.date,
      'Satış (₺)': item.sales,
      'Sipariş Sayısı': item.orders
    })));
    XLSX.utils.book_append_sheet(workbook, salesSheet, 'Günlük Satışlar');

    // Sales by category sheet
    const categorySheet = XLSX.utils.json_to_sheet(salesByCategoryData.map(item => ({
      'Kategori': item.name,
      'Satış (₺)': item.value,
      'Yüzde (%)': item.percentage
    })));
    XLSX.utils.book_append_sheet(workbook, categorySheet, 'Kategori Satışları');

    // Top products sheet
    const productsSheet = XLSX.utils.json_to_sheet(topProductsData.map(item => ({
      'Ürün Adı': item.name,
      'Satış (₺)': item.sales,
      'Adet': item.units,
      'Kar (₺)': item.profit
    })));
    XLSX.utils.book_append_sheet(workbook, productsSheet, 'En İyi Ürünler');

    // Category reports sheet
    const categoryReportsSheet = XLSX.utils.json_to_sheet(categoryReportsData.map(item => ({
      'Kategori': item.category,
      'Ürün Sayısı': item.productCount,
      'Toplam Adet': item.totalQuantity,
      'Stok Değeri (₺)': item.totalValue,
      'Satış Tutarı (₺)': item.totalSales,
      'Ortalama Fiyat (₺)': item.avgPrice
    })));
    XLSX.utils.book_append_sheet(workbook, categoryReportsSheet, 'Kategori Raporları');

    // Product quantity sheet
    const productQuantitySheet = XLSX.utils.json_to_sheet(productQuantityData.map(item => ({
      'Ürün Adı': item.name,
      'Kategori': item.category,
      'Stok Adedi': item.quantity,
      'Stok Değeri (₺)': item.value
    })));
    XLSX.utils.book_append_sheet(workbook, productQuantitySheet, 'Ürün Stok Adetleri');

    // Inventory value sheet
    const inventoryValueSheet = XLSX.utils.json_to_sheet(inventoryValueData.map(item => ({
      'Kategori': item.category,
      'Stok Değeri (₺)': item.value,
      'Yüzde (%)': item.percentage,
      'Ürün Sayısı': item.productCount
    })));
    XLSX.utils.book_append_sheet(workbook, inventoryValueSheet, 'Kategori Stok Değeri');

    // Low stock sheet
    const lowStockSheet = XLSX.utils.json_to_sheet(lowStockData.map(item => ({
      'Ürün Adı': item.name,
      'Ürün Kodu': item.code,
      'Kategori': item.category,
      'Mevcut Stok': item.currentStock,
      'Sipariş Noktası': item.reorderPoint,
      'Değer (₺)': item.value
    })));
    XLSX.utils.book_append_sheet(workbook, lowStockSheet, 'Düşük Stok');

    // Price performance sheet
    const pricePerformanceSheet = XLSX.utils.json_to_sheet(pricePerformanceData.map(item => ({
      'Ürün Adı': item.name,
      'Kategori': item.category,
      'Satış Fiyatı (₺)': item.price,
      'Maliyet (₺)': item.cost,
      'Kar Marjı (%)': item.margin
    })));
    XLSX.utils.book_append_sheet(workbook, pricePerformanceSheet, 'Fiyat Performansı');

    // Inventory turnover sheet
    const turnoverSheet = XLSX.utils.json_to_sheet(inventoryTurnoverData.map(item => ({
      'Kategori': item.category,
      'Devir Hızı': item.turnover,
      'Durum': getTurnoverLabel(item.status)
    })));
    XLSX.utils.book_append_sheet(workbook, turnoverSheet, 'Stok Devir');

    // Stock coverage sheet
    const stockCoverageSheet = XLSX.utils.json_to_sheet(
      (stockCoverage ? [...stockCoverage.rows, stockCoverage.total] : []).map(item => ({
        'Kategori': item.category,
        [`Son ${STOCK_COVERAGE_PERIOD_MONTHS} Ay Satış (Adet)`]: item.soldQty,
        [`Son ${STOCK_COVERAGE_PERIOD_MONTHS} Ay Satış (₺)`]: item.salesAmount,
        'Aylık Ort. Satış (Adet)': Number(item.monthlyAvgQty.toFixed(1)),
        'Mevcut Stok (Adet)': item.stockQty,
        'Stok Değeri (₺)': item.stockValue,
        'Stok Süresi (Ay)': item.monthsOfStock === null ? 'Satış yok' : Number(item.monthsOfStock.toFixed(1))
      }))
    );
    XLSX.utils.book_append_sheet(workbook, stockCoverageSheet, 'Stok Süresi');

    // Dead stock sheet
    const deadStockSheet = XLSX.utils.json_to_sheet(allDeadStockData.map(item => ({
      'Ürün Adı': item.name,
      'Ürün Kodu': item.code,
      'Kategori': item.category,
      'Miktar': item.quantity,
      'Hareketsiz Gün': item.daysInStock,
      'Son Aktivite': item.lastActivityAt ? new Date(item.lastActivityAt).toLocaleDateString('tr-TR') : '-',
      'Değer (₺)': item.value
    })));
    XLSX.utils.book_append_sheet(workbook, deadStockSheet, 'Ölü Stok');

    // On-hold items sheet
    const onHoldSheet = XLSX.utils.json_to_sheet(onHoldData.map(item => ({
      'Ürün Adı': item.productName,
      'Müşteri': item.customerName,
      'Telefon': item.customerPhone,
      'Kategori': item.category,
      'Miktar': item.quantity,
      'Değer (₺)': item.value,
      'Beklemede Gün': item.daysOnHold,
      'Beklemede Başlangıç': new Date(item.onHoldDate).toLocaleDateString('tr-TR')
    })));
    XLSX.utils.book_append_sheet(workbook, onHoldSheet, 'Beklemede Ürünler');

    // On-hold by category sheet
    const onHoldCategorySheet = XLSX.utils.json_to_sheet(onHoldByCategoryData.map(item => ({
      'Kategori': item.category,
      'Ürün Sayısı': item.count,
      'Toplam Adet': item.quantity,
      'Toplam Değer (₺)': item.value,
      'Ortalama Bekleme (Gün)': item.avgDuration
    })));
    XLSX.utils.book_append_sheet(workbook, onHoldCategorySheet, 'Beklemede Kategori');

    // On-hold by duration sheet
    const onHoldDurationSheet = XLSX.utils.json_to_sheet(onHoldByDurationData.map(item => ({
      'Süre Aralığı': item.range,
      'Ürün Sayısı': item.count,
      'Yüzde (%)': item.percentage
    })));
    XLSX.utils.book_append_sheet(workbook, onHoldDurationSheet, 'Beklemede Süre');

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
    const filename = `rapor-analitik-${timestamp}.xlsx`;
    
    XLSX.writeFile(workbook, filename);
    
    toast({
      title: "Rapor İndirildi",
      description: "Excel dosyası başarıyla oluşturuldu",
    });
  };

  const handleForecastUpload = () => {
    toast({
      title: "Tahmin Yükleme",
      description: "Dosya yükleme özelliği geliştirme aşamasında",
    });
  };

  const refreshData = async () => {
    await fetchAnalyticsData();
    if (hasData) {
      toast({
        title: "Veriler Yenilendi",
        description: "Analitik veriler başarıyla güncellendi",
      });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Raporlar ve Analitik</h1>
            <p className="text-muted-foreground">Satış ve stok analizi</p>
          </div>

          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <BarChart3 className="w-8 h-8 text-muted-foreground animate-spin" />
              </div>
              <h3 className="text-lg font-medium mb-2">Veriler Yükleniyor...</h3>
              <p className="text-muted-foreground text-sm">
                Analitik verileriniz hazırlanıyor.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (!hasData) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Raporlar ve Analitik</h1>
            <p className="text-muted-foreground">Satış ve stok analizi</p>
          </div>

          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <BarChart3 className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">Henüz Veri Bulunmuyor</h3>
              <p className="text-muted-foreground text-sm mb-6 max-w-md">
                Raporları görüntülemek için sistemde satış veya ürün verisi bulunmuyor. 
                Satış yapmaya veya ürün eklemeye başladığınızda analitik veriler burada görünecektir.
              </p>
              {error && (
                <div className="mb-4 text-red-600 text-sm">
                  {error}
                </div>
              )}
              <Button onClick={refreshData} disabled={loading}>
                <Upload className="w-4 h-4 mr-2" />
                Verileri Yenile
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Raporlar ve Analitik</h1>
            <p className="text-muted-foreground">Satış ve stok analizi</p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={refreshData} disabled={loading}>
              <Upload className="w-4 h-4 mr-2" />
              Yenile
            </Button>
            <Button variant="outline" onClick={handleForecastUpload}>
              <Upload className="w-4 h-4 mr-2" />
              Tahmin Yükle
            </Button>
            <Button onClick={handleExportExcel}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel İndir
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtreler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="location">Lokasyon</Label>
                  <Select
                    value={selectedLocation || ''}
                    onValueChange={(value) => {
                      setSelectedLocation(value);
                      if (canSwitchLocation) {
                        const nextLocation = availableLocations.find((loc) => loc.id === value);
                        if (nextLocation) {
                          setCurrentLocation(nextLocation);
                        }
                      }
                    }}
                    disabled={!canSwitchLocation}
                  >
                    <SelectTrigger id="location">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableLocations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {location.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="dateRange">Tarih Aralığı</Label>
                  <Select value={dateRange} onValueChange={(value) => {
                    setDateRange(value);
                    // Reset custom dates applied when changing away from custom
                    if (value !== 'custom') {
                      setCustomDatesApplied(false);
                    }
                  }}>
                    <SelectTrigger id="dateRange">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="thisMonth">Bu Ay</SelectItem>
                      <SelectItem value="lastMonth">Geçen Ay</SelectItem>
                      <SelectItem value="lastYear">Geçen Yıl</SelectItem>
                      <SelectItem value="7">Son 7 Gün</SelectItem>
                      <SelectItem value="30">Son 30 Gün</SelectItem>
                      <SelectItem value="90">Son 3 Ay</SelectItem>
                      <SelectItem value="365">Son 1 Yıl</SelectItem>
                      <SelectItem value="custom">Özel Tarih</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button 
                    variant="outline" 
                    onClick={handleResetFilters}
                    className="w-full"
                  >
                    Filtreleri Sıfırla
                  </Button>
                </div>
              </div>
              {dateRange === 'custom' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t">
                  <div>
                    <Label htmlFor="customDateFrom">Başlangıç Tarihi</Label>
                    <Input
                      id="customDateFrom"
                      type="date"
                      value={customDateFrom}
                      onChange={(e) => {
                        setCustomDateFrom(e.target.value);
                        setCustomDatesApplied(false);
                      }}
                      placeholder="Başlangıç tarihi seçin..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="customDateTo">Bitiş Tarihi</Label>
                    <Input
                      id="customDateTo"
                      type="date"
                      value={customDateTo}
                      onChange={(e) => {
                        setCustomDateTo(e.target.value);
                        setCustomDatesApplied(false);
                      }}
                      placeholder="Bitiş tarihi seçin..."
                      min={customDateFrom}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={handleCustomDateQuery}
                      className="w-full"
                      disabled={!customDateFrom || !customDateTo}
                    >
                      Sorgula
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <DollarSign className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : `₺${keyMetrics.totalSales.toLocaleString('tr-TR')}`}
                  </p>
                  <p className="text-sm text-muted-foreground">Toplam Satış</p>
                  <div className="flex items-center gap-1 text-green-500 text-xs mt-1">
                    <TrendingUp className="w-3 h-3" />
                    {getDateRangeLabel(dateRange, customDateFrom, customDateTo)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Package className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : `₺${keyMetrics.totalInventoryValue.toLocaleString('tr-TR')}`}
                  </p>
                  <p className="text-sm text-muted-foreground">Son Alınan Fiyata Göre Stok Değeri</p>
                  <div className="flex items-center gap-1 text-blue-500 text-xs mt-1">
                    <Package className="w-3 h-3" />
                    {keyMetrics.totalProducts} ürün
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Package className="w-8 h-8 text-indigo-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : `₺${keyMetrics.marsInventoryValue.toLocaleString('tr-TR')}`}
                  </p>
                  <p className="text-sm text-muted-foreground">Güncel Mars Fiyatlarına Göre Stok Değeri</p>
                  <div className="flex items-center gap-1 text-indigo-500 text-xs mt-1">
                    <Package className="w-3 h-3" />
                    Kar hesaplama ayarı: {profitSettings.marsPriceTier === 'pesin' ? 'Peşin' : `${profitSettings.marsPriceTier} gün vadeli`}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <BarChart3 className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : keyMetrics.totalUnitsInStock.toLocaleString('tr-TR')}
                  </p>
                  <p className="text-sm text-muted-foreground">Toplam Adet</p>
                  <div className="flex items-center gap-1 text-purple-500 text-xs mt-1">
                    <BarChart3 className="w-3 h-3" />
                    Stokta
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <TrendingUp className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : `%${keyMetrics.averageMargin}`}
                  </p>
                  <p className="text-sm text-muted-foreground">Ortalama Kar Marjı</p>
                  <div className="flex items-center gap-1 text-orange-500 text-xs mt-1">
                    <TrendingUp className="w-3 h-3" />
                    Tüm ürünler
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Sales Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <TrendingUp className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : `₺${keyMetrics.grossProfit.toLocaleString('tr-TR')}`}
                  </p>
                  <p className="text-sm text-muted-foreground">Brüt Kar</p>
                  <div className="flex items-center gap-1 text-green-600 text-xs mt-1">
                    <TrendingUp className="w-3 h-3" />
                    {getDateRangeLabel(dateRange, customDateFrom, customDateTo)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Package className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : keyMetrics.inventoryTurnover}
                  </p>
                  <p className="text-sm text-muted-foreground">Stok Devir Hızı</p>
                  <div className="flex items-center gap-1 text-orange-500 text-xs mt-1">
                    <Package className="w-3 h-3" />
                    Ortalama
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : `₺${keyMetrics.deadStockValue.toLocaleString('tr-TR')}`}
                  </p>
                  <p className="text-sm text-muted-foreground">Ölü Stok Değeri</p>
                  <div className="flex items-center gap-1 text-red-500 text-xs mt-1">
                    <AlertTriangle className="w-3 h-3" />
                    {DEAD_STOCK_THRESHOLD_DAYS}+ gün hareketsiz
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <AlertTriangle className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : lowStockData.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Düşük Stok</p>
                  <div className="flex items-center gap-1 text-yellow-500 text-xs mt-1">
                    <AlertTriangle className="w-3 h-3" />
                    Uyarı
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* On-Hold Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Package className="w-8 h-8 text-indigo-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : keyMetrics.onHoldCount}
                  </p>
                  <p className="text-sm text-muted-foreground">Beklemede Adet</p>
                  <div className="flex items-center gap-1 text-indigo-500 text-xs mt-1">
                    <Package className="w-3 h-3" />
                    Adet
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <DollarSign className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : `₺${keyMetrics.onHoldValue.toLocaleString('tr-TR')}`}
                  </p>
                  <p className="text-sm text-muted-foreground">Beklemede Değer</p>
                  <div className="flex items-center gap-1 text-purple-500 text-xs mt-1">
                    <DollarSign className="w-3 h-3" />
                    Toplam değer
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Calendar className="w-8 h-8 text-teal-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : `${keyMetrics.avgOnHoldDuration} gün`}
                  </p>
                  <p className="text-sm text-muted-foreground">Ort. Bekleme Süresi</p>
                  <div className="flex items-center gap-1 text-teal-500 text-xs mt-1">
                    <Calendar className="w-3 h-3" />
                    Ortalama
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales by Day */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Günlük Satışlar (Son 7 Gün)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={salesByDayData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'sales' ? `₺${value.toLocaleString('tr-TR')}` : value,
                      name === 'sales' ? 'Satış' : 'Sipariş'
                    ]}
                  />
                  <Bar dataKey="sales" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Sales by Category */}
          <Card>
            <CardHeader>
              <CardTitle>Kategoriye Göre Satışlar</CardTitle>
            </CardHeader>
            <CardContent>
              {salesByCategoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={salesByCategoryData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percentage }) => `${name} %${percentage}`}
                    >
                      {salesByCategoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `₺${value.toLocaleString('tr-TR')}`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="text-center">
                    <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Henüz kategori satış verisi bulunmuyor</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Visits Trend */}
        <Card>
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Ziyaret Trendi
            </CardTitle>
            <Select
              value={visitsChartView}
              onValueChange={(value: 'weekly' | 'monthly') => setVisitsChartView(value)}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Haftalık</SelectItem>
                <SelectItem value="monthly">Aylık</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {visitsTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={visitsTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis allowDecimals={false} />
                  <Tooltip
                    formatter={(value) => [value, 'Ziyaret']}
                    labelFormatter={(label) =>
                      visitsChartView === 'weekly' ? `Hafta: ${label}` : `Ay: ${label}`
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="visits"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-center">
                  <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Seçili tarih aralığında ziyaret verisi bulunmuyor
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card>
          <CardHeader>
            <CardTitle>En İyi Ürünler</CardTitle>
          </CardHeader>
                      <CardContent>
              <div className="space-y-4">
                {topProductsData.length > 0 ? (
                  topProductsData.map((product, index) => (
                    <div key={product.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-medium">{product.name}</h4>
                          <p className="text-sm text-muted-foreground">{product.units} adet satıldı</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">₺{product.sales.toLocaleString('tr-TR')}</p>
                        <p className="text-sm text-green-600">+₺{product.profit.toLocaleString('tr-TR')} kar</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Henüz satış verisi bulunmuyor</p>
                  </div>
                )}
              </div>
            </CardContent>
        </Card>

        {/* Category Sales Breakdown */}
        <Card className="border-2 border-orange-500/30 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-orange-500/10 via-orange-400/10 to-orange-500/10 border-b-2 border-orange-500/30">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              <span className="text-orange-700 dark:text-orange-300">Kategoriye Göre Satılan Ürünler</span>
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              {getDateRangeLabel(dateRange, customDateFrom, customDateTo)} tarih aralığında satılan ürünler
            </p>
          </CardHeader>
          <CardContent className="pt-6">
            {categorySalesBreakdown.length > 0 ? (
              <div className="space-y-3">
                {categorySalesBreakdown.map((categoryData, categoryIndex) => {
                  const isExpanded = expandedCategories.has(categoryData.category);
                  // Generate different orange shades for variety
                  const orangeShades = [
                    'border-orange-500 bg-orange-50/50 dark:bg-orange-950/20',
                    'border-orange-400 bg-orange-50/50 dark:bg-orange-950/20',
                    'border-orange-600 bg-orange-50/50 dark:bg-orange-950/20',
                    'border-orange-500 bg-orange-50/50 dark:bg-orange-950/20',
                  ];
                  const shadeIndex = categoryIndex % orangeShades.length;
                  const borderColor = orangeShades[shadeIndex].split(' ')[0];
                  
                  return (
                    <Collapsible
                      key={categoryData.category}
                      open={isExpanded}
                      onOpenChange={(open) => {
                        const newExpanded = new Set(expandedCategories);
                        if (open) {
                          newExpanded.add(categoryData.category);
                        } else {
                          newExpanded.delete(categoryData.category);
                        }
                        setExpandedCategories(newExpanded);
                      }}
                    >
                      <div className={`border-2 ${borderColor} rounded-lg overflow-hidden ${orangeShades[shadeIndex]} shadow-md hover:shadow-lg transition-all`}>
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between p-4 hover:bg-orange-100/50 dark:hover:bg-orange-900/20 transition-colors">
                            <div className="flex items-center gap-4 flex-1">
                              <div className="w-10 h-10 rounded-lg bg-orange-500/20 dark:bg-orange-500/30 flex items-center justify-center flex-shrink-0 border border-orange-500/30">
                                <Package className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                              </div>
                              <div className="flex-1 text-left">
                                <h3 className="text-lg font-bold text-orange-900 dark:text-orange-100">{categoryData.category}</h3>
                                <p className="text-sm text-orange-700/70 dark:text-orange-300/70">
                                  {categoryData.products.length} ürün • Toplam {categoryData.totalQuantity} adet satıldı
                                </p>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge className="text-base px-4 py-1.5 font-semibold bg-orange-500 text-white hover:bg-orange-600 border-orange-600">
                                  {categoryData.totalQuantity} adet
                                </Badge>
                                <ChevronDown 
                                  className={`w-5 h-5 text-orange-600 dark:text-orange-400 transition-transform duration-200 ${
                                    isExpanded ? 'rotate-180' : ''
                                  }`}
                                />
                              </div>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="border-t-2 border-orange-500/30 bg-orange-50/30 dark:bg-orange-950/10 p-4 space-y-2">
                            {categoryData.products.map((product: any, productIndex: number) => (
                              <div 
                                key={`${categoryData.category}-${product.name}-${productIndex}`}
                                className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 border-2 border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-950/30 transition-colors shadow-sm"
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="w-8 h-8 rounded-full bg-orange-500/20 dark:bg-orange-500/30 border-2 border-orange-500/40 text-orange-700 dark:text-orange-300 flex items-center justify-center font-bold text-sm flex-shrink-0">
                                    {productIndex + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold truncate text-orange-900 dark:text-orange-100">{product.name}</p>
                                    <p className="text-xs text-orange-700/70 dark:text-orange-300/70">
                                      ₺{product.sales.toLocaleString('tr-TR')} satış değeri
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0 ml-4">
                                  <p className="font-bold text-base text-orange-600 dark:text-orange-400">{product.quantity} adet</p>
                                  {product.profit > 0 && (
                                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                                      +₺{product.profit.toLocaleString('tr-TR')} kar
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-orange-400/50 mx-auto mb-4" />
                <p className="text-muted-foreground text-lg">Seçilen tarih aralığında satış verisi bulunmuyor</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stock Coverage by Category */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Kategoriye Göre Stok Süresi
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Stok süresi = Mevcut stok / Aylık ortalama satış adedi
            </p>
            <p className="text-xs text-muted-foreground mt-1 italic">
              * Tarih filtresinden bağımsız olarak son {STOCK_COVERAGE_PERIOD_MONTHS} ayın satışları kullanılmaktadır
            </p>
          </CardHeader>
          <CardContent>
            {stockCoverage && stockCoverage.rows.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kategori</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Son {STOCK_COVERAGE_PERIOD_MONTHS} Ay Satış</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Satış Tutarı</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Aylık Ort.</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Mevcut Stok</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Stok Değeri</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Stok Süresi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockCoverage.rows.map((item) => (
                    <TableRow key={item.category}>
                      <TableCell className="font-medium">{item.category}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{item.soldQty.toLocaleString('tr-TR')} adet</TableCell>
                      <TableCell className="text-right whitespace-nowrap">₺{item.salesAmount.toLocaleString('tr-TR')}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {item.monthlyAvgQty.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} adet
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">{item.stockQty.toLocaleString('tr-TR')} adet</TableCell>
                      <TableCell className="text-right whitespace-nowrap">₺{item.stockValue.toLocaleString('tr-TR')}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Badge variant={item.monthsOfStock === null ? 'destructive' : 'outline'}>
                          {formatMonthsOfStock(item.monthsOfStock)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell className="font-bold">{stockCoverage.total.category}</TableCell>
                    <TableCell className="text-right whitespace-nowrap font-bold">{stockCoverage.total.soldQty.toLocaleString('tr-TR')} adet</TableCell>
                    <TableCell className="text-right whitespace-nowrap font-bold">₺{stockCoverage.total.salesAmount.toLocaleString('tr-TR')}</TableCell>
                    <TableCell className="text-right whitespace-nowrap font-bold">
                      {stockCoverage.total.monthlyAvgQty.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} adet
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap font-bold">{stockCoverage.total.stockQty.toLocaleString('tr-TR')} adet</TableCell>
                    <TableCell className="text-right whitespace-nowrap font-bold">₺{stockCoverage.total.stockValue.toLocaleString('tr-TR')}</TableCell>
                    <TableCell className="text-right whitespace-nowrap font-bold">
                      {formatMonthsOfStock(stockCoverage.total.monthsOfStock)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            ) : (
              <div className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Henüz stok veya satış verisi bulunmuyor</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inventory Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Inventory Turnover */}
          <Card>
            <CardHeader>
              <CardTitle>Stok Devir Analizi</CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Devir hızı = Satılan miktar / Ortalama stok miktarı
              </p>
              <p className="text-xs text-muted-foreground mt-1 italic">
                * Son 12 ayın verileri kullanılmaktadır
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {inventoryTurnoverData.length > 0 ? (
                  inventoryTurnoverData.map((item) => (
                    <div key={item.category} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{item.category}</span>
                        <Badge variant={getTurnoverBadgeVariant(item.status)}>
                          {getTurnoverLabel(item.status)}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                        <div>
                          <span className="font-medium text-foreground">{item.turnover.toFixed(2)}</span>
                          <p>Devir Hızı</p>
                        </div>
                        <div>
                          <span className="font-medium text-foreground">{item.soldQty}</span>
                          <p>Satılan Adet</p>
                        </div>
                        <div>
                          <span className="font-medium text-foreground">{item.avgInventory}</span>
                          <p>Ort. Stok</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Henüz ürün verisi bulunmuyor</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Dead Stock */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Ölü Stok Listesi
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/reports/dead-stock')}
                disabled={allDeadStockData.length === 0}
              >
                Tümünü Gör ({allDeadStockData.length})
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {deadStockData.length > 0 ? (
                  <>
                    <p className="text-xs text-muted-foreground">
                      İlk {deadStockData.length} kayıt listeleniyor.
                    </p>
                    {deadStockData.map((item) => (
                      <div key={item.id} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-sm">{item.name}</h4>
                            <p className="text-xs text-muted-foreground">{item.code}</p>
                            <p className="text-xs text-muted-foreground">{item.category}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Miktar: <span className="font-medium">{item.quantity}</span> adet
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">₺{item.value.toLocaleString('tr-TR')}</p>
                            <p className="text-xs text-red-600">{item.daysInStock} gün hareketsiz</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Ölü stok bulunamadı</p>
                    <p className="text-xs text-muted-foreground mt-1">Bu iyi bir durum!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Product Analytics Section */}
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold">Ürün Analitiği</h2>
          </div>

          {/* Category-Based Reports */}
          <Card>
            <CardHeader>
              <CardTitle>Kategoriye Göre Detaylı Analiz</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {categoryReportsData.length > 0 ? (
                  categoryReportsData.map((category, index) => {
                    const colorScheme = getCategoryColor(index);
                    return (
                      <div 
                        key={category.category} 
                        className={`p-5 border-2 rounded-lg ${colorScheme.bg} ${colorScheme.border} transition-all hover:shadow-md`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${colorScheme.accent}`}></div>
                            <h4 className={`font-bold text-lg ${colorScheme.text}`}>{category.category}</h4>
                          </div>
                          <Badge variant="outline" className="bg-white">{category.productCount} ürün</Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-white/60 p-3 rounded-lg border border-white/80">
                            <p className="text-xs text-muted-foreground mb-1">Toplam Adet</p>
                            <p className={`font-bold text-lg ${colorScheme.text}`}>
                              {category.totalQuantity.toLocaleString('tr-TR')}
                            </p>
                          </div>
                          <div className="bg-white/60 p-3 rounded-lg border border-white/80">
                            <p className="text-xs text-muted-foreground mb-1">Stok Değeri</p>
                            <p className={`font-bold text-lg ${colorScheme.text}`}>
                              ₺{category.totalValue.toLocaleString('tr-TR')}
                            </p>
                          </div>
                          <div className="bg-white/60 p-3 rounded-lg border border-white/80">
                            <p className="text-xs text-muted-foreground mb-1">Satış Tutarı</p>
                            <p className={`font-bold text-lg ${colorScheme.text}`}>
                              ₺{category.totalSales.toLocaleString('tr-TR')}
                            </p>
                          </div>
                          <div className="bg-white/60 p-3 rounded-lg border border-white/80">
                            <p className="text-xs text-muted-foreground mb-1">Ort. Fiyat</p>
                            <p className={`font-bold text-lg ${colorScheme.text}`}>
                              ₺{category.avgPrice.toLocaleString('tr-TR')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Henüz kategori verisi bulunmuyor</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Product Quantity & Inventory Value Reports */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Products by Quantity */}
            <Card>
              <CardHeader>
                <CardTitle>En Çok Stokta Olan Ürünler</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {productQuantityData.length > 0 ? (
                    productQuantityData.slice(0, 10).map((product, index) => (
                      <div key={`${product.name}-${index}`} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <h4 className="font-medium text-sm">{product.name}</h4>
                          <p className="text-xs text-muted-foreground">{product.category}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{product.quantity} adet</p>
                          <p className="text-xs text-muted-foreground">₺{product.value.toLocaleString('tr-TR')}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Henüz stok verisi bulunmuyor</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Inventory Value by Category */}
            <Card>
              <CardHeader>
                <CardTitle>Kategoriye Göre Stok Değeri</CardTitle>
              </CardHeader>
              <CardContent>
                {inventoryValueData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={inventoryValueData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ category, percentage }) => `${category} %${percentage}`}
                      >
                        {inventoryValueData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getRandomColor()} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `₺${value.toLocaleString('tr-TR')}`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px]">
                    <div className="text-center">
                      <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Stok değeri verisi bulunmuyor</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Stock Alerts & Price Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Low Stock Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Düşük Stok Uyarıları
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {lowStockData.length > 0 ? (
                    lowStockData.map((item) => (
                      <div key={item.id} className="p-3 border rounded-lg border-yellow-200 bg-yellow-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-sm">{item.name}</h4>
                            <p className="text-xs text-muted-foreground">{item.code} • {item.category}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-yellow-700">
                              {item.currentStock} / {item.reorderPoint}
                            </p>
                            <p className="text-xs text-muted-foreground">₺{item.value.toLocaleString('tr-TR')}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Düşük stok uyarısı yok</p>
                      <p className="text-xs text-muted-foreground mt-1">Tüm ürünler yeterli stoğa sahip!</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Price Performance Analysis */}
            <Card>
              <CardHeader>
                <CardTitle>Fiyat Performansı (En Yüksek Marj)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pricePerformanceData.length > 0 ? (
                    pricePerformanceData.slice(0, 10).map((product, index) => (
                      <div key={`${product.name}-${index}`} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <h4 className="font-medium text-sm">{product.name}</h4>
                          <p className="text-xs text-muted-foreground">{product.category}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">%{product.margin}</p>
                          <p className="text-xs text-muted-foreground">
                            ₺{product.price.toLocaleString('tr-TR')} / ₺{product.cost.toLocaleString('tr-TR')}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Fiyat performansı verisi bulunmuyor</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Additional Product Insights */}
          <Card>
            <CardHeader>
              <CardTitle>Ürün İstatistikleri Özeti</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 border rounded-lg">
                  <Package className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{keyMetrics.totalProducts}</p>
                  <p className="text-sm text-muted-foreground">Toplam Ürün Çeşidi</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <BarChart3 className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{categoryReportsData.length}</p>
                  <p className="text-sm text-muted-foreground">Aktif Kategori</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">%{keyMetrics.averageMargin}</p>
                  <p className="text-sm text-muted-foreground">Ortalama Kar Marjı</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* On-Hold Analytics Section */}
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-6 h-6 text-indigo-500" />
            <h2 className="text-2xl font-bold">Beklemedeki Ürünler Analizi</h2>
          </div>

          {/* On-Hold Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* On-Hold by Duration */}
            <Card>
              <CardHeader>
                <CardTitle>Bekleme Süresine Göre Dağılım</CardTitle>
              </CardHeader>
              <CardContent>
                {onHoldByDurationData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={onHoldByDurationData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" />
                      <YAxis />
                      <Tooltip formatter={(value, name) => [value, name === 'count' ? 'Ürün Sayısı' : name]} />
                      <Bar dataKey="count" fill="#6366f1" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px]">
                    <div className="text-center">
                      <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Beklemede ürün bulunmuyor</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* On-Hold by Category */}
            <Card>
              <CardHeader>
                <CardTitle>Kategoriye Göre Beklemede Değer</CardTitle>
              </CardHeader>
              <CardContent>
                {onHoldByCategoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={onHoldByCategoryData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ category, value }) => `${category}: ₺${value.toLocaleString('tr-TR')}`}
                      >
                        {onHoldByCategoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getRandomColor()} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `₺${value.toLocaleString('tr-TR')}`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px]">
                    <div className="text-center">
                      <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Kategori bazında beklemede ürün yok</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Category Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>Kategoriye Göre Beklemede Ürün İstatistikleri</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {onHoldByCategoryData.length > 0 ? (
                  onHoldByCategoryData.map((category) => (
                    <div key={category.category} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-lg">{category.category}</h4>
                        <Badge variant="outline">{category.count} ürün</Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Toplam Adet</p>
                          <p className="font-semibold">{category.quantity} adet</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Toplam Değer</p>
                          <p className="font-semibold">₺{category.value.toLocaleString('tr-TR')}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Ürün Çeşidi</p>
                          <p className="font-semibold">{category.count} çeşit</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Ort. Bekleme</p>
                          <p className="font-semibold">{category.avgDuration} gün</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Beklemede kategori verisi bulunmuyor</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Detailed On-Hold Items List */}
          <Card>
            <CardHeader>
              <CardTitle>Beklemedeki Ürünler Detayı</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {onHoldData.length > 0 ? (
                  onHoldData.slice(0, 15).map((item) => (
                    <div key={item.id} className="p-4 border rounded-lg">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-medium">{item.productName}</h4>
                          <p className="text-sm text-muted-foreground">
                            {item.customerName} • {item.customerPhone}
                          </p>
                          <p className="text-xs text-muted-foreground">{item.category}</p>
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center gap-4 text-sm">
                          <div className="text-center">
                            <p className="font-semibold">{item.quantity} adet</p>
                            <p className="text-xs text-muted-foreground">Miktar</p>
                          </div>
                          <div className="text-center">
                            <p className="font-semibold">₺{item.value.toLocaleString('tr-TR')}</p>
                            <p className="text-xs text-muted-foreground">Değer</p>
                          </div>
                          <div className="text-center">
                            <p className={`font-semibold ${
                              item.daysOnHold > 30 ? 'text-red-600' : 
                              item.daysOnHold > 7 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {item.daysOnHold} gün
                            </p>
                            <p className="text-xs text-muted-foreground">Beklemede</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Beklemede ürün bulunmuyor</p>
                    <p className="text-xs text-muted-foreground mt-1">Tüm ürünler satıldı veya iade edildi!</p>
                  </div>
                )}
              </div>
              {onHoldData.length > 15 && (
                <div className="mt-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    {onHoldData.length - 15} ürün daha var. Daha fazla görmek için Excel raporu indirin.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* On-Hold Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Beklemede Ürünler Özeti</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center p-4 border rounded-lg">
                  <Package className="w-8 h-8 text-indigo-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{keyMetrics.onHoldCount}</p>
                  <p className="text-sm text-muted-foreground">Toplam Bekleyen Ürün</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <DollarSign className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">₺{keyMetrics.onHoldValue.toLocaleString('tr-TR')}</p>
                  <p className="text-sm text-muted-foreground">Toplam Beklemede Değer</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <Calendar className="w-8 h-8 text-teal-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{keyMetrics.avgOnHoldDuration} gün</p>
                  <p className="text-sm text-muted-foreground">Ortalama Bekleme Süresi</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <BarChart3 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{onHoldByCategoryData.length}</p>
                  <p className="text-sm text-muted-foreground">Etkilenen Kategori</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}